// Passport picker with search + HID barcode scanner support.
// Supports fast typing patterns from USB HID barcode guns (which paste
// the code and press Enter). Also usable via manual typing.
import { useEffect, useRef, useState } from "react";
import { api, errorToMessage } from "@/lib/apiClient";

export default function PassportPicker({
  onPick,                    // (passport) => void
  filterFn,                  // (passport) => boolean, optional
  placeholder = "Scan or type Passport ID (RP…) / serial",
  autoFocus = true,
  testId = "passport-picker",
}) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const search = async (text) => {
    if (!text || text.trim().length < 2) { setHits([]); return; }
    setLoading(true); setError(null);
    try {
      const { data } = await api.get("/search", { params: { q: text.trim() } });
      let items = (data.items || []).filter((it) => it.type === "passport");
      if (filterFn) items = items.filter((it) => filterFn(it));
      setHits(items.slice(0, 8));
    } catch (e) { setError(errorToMessage(e, "Search failed")); }
    finally { setLoading(false); }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(v), 200);
  };

  const pick = (it) => {
    onPick && onPick({
      id: it.id,
      passport_uid: it.meta?.passport_uid,
      title: it.title,
      subtitle: it.subtitle,
      item_type: it.meta?.item_type,
      status: it.meta?.status,
    });
    setQ(""); setHits([]);
    if (inputRef.current) inputRef.current.focus();
  };

  const onEnter = async (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    // If exactly one hit — pick it. Otherwise re-search.
    if (hits.length === 1) { pick(hits[0]); return; }
    // If the user typed a full RP UID, try to match directly.
    const clean = q.trim();
    if (/^RP\d{6}\d{4}$/i.test(clean)) {
      await search(clean);
      // After state settles the useEffect on hits could auto-pick; do it manually via ref
      const { data } = await api.get("/search", { params: { q: clean } });
      const exact = (data.items || []).find(
        (it) => it.type === "passport" && it.meta?.passport_uid?.toLowerCase() === clean.toLowerCase()
      );
      if (exact && (!filterFn || filterFn(exact))) pick(exact);
    }
  };

  return (
    <div className="relative w-full" data-testid={testId}>
      <input
        ref={inputRef}
        value={q}
        onChange={onChange}
        onKeyDown={onEnter}
        placeholder={placeholder}
        className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
        data-testid={`${testId}-input`}
      />
      {error && <div className="mt-1 text-xs text-rose-700">{error}</div>}
      {loading && <div className="mt-1 text-xs text-slate-500">Searching…</div>}
      {hits.length > 0 && (
        <ul className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg"
            data-testid={`${testId}-results`}>
          {hits.map((it) => (
            <li key={it.id}>
              <button type="button" onClick={() => pick(it)}
                      className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-slate-50"
                      data-testid={`${testId}-hit-${it.meta?.passport_uid || it.id}`}>
                <span className="font-mono text-blue-700 font-semibold">
                  {it.meta?.passport_uid || "Passport"}
                  {it.meta?.item_type && it.meta.item_type !== "laptop" && (
                    <span className="ml-1.5 inline-flex items-center rounded-full bg-indigo-100 border border-indigo-200 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider text-indigo-800">
                      {it.meta.item_type}
                    </span>
                  )}
                  <span className="ml-2 text-[10px] font-medium text-slate-500">{it.meta?.status}</span>
                </span>
                <span className="text-xs text-slate-700">{it.title}</span>
                <span className="text-[11px] text-slate-500">{it.subtitle}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
